import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import { TrashIcon } from '@heroicons/react/24/outline';


const POViewPage = () => {
  const { id } = useParams(); // PO ID from URL
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const [po, setPO] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null); // e.g., 'Cancelled', 'Closed', etc.


  // Add item state
  const [newItem, setNewItem] = useState({
    device_id: '',
    quantity: '',
    price: '',
  });

  // Optional: list of products for dropdown
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchPO();
    fetchPOItems();
    fetchProducts(); // For device dropdown
  }, [id]);

  // Fetch PO header info
  const fetchPO = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/inventory_crud.php?po_id=${id}`);
      console.log('PO Response:', res.data);
      if (Array.isArray(res.data) && res.data[0] === 'success') {
        setPO(res.data[1]);
      } else {
        toast.error('Failed to load PO');
      }
    } catch {
      toast.error('Error fetching PO');
    } finally {
      setLoading(false);
    }
  };

  // Fetch PO line items
  const fetchPOItems = async () => {
    try {
      const res = await axios.get(`${apiUrl}/inventory_crud.php?po_items=1&po_id=${id}`);
      if (Array.isArray(res.data) && res.data[0] === 'success') {
        setItems(res.data[1]);
      } else {
        setItems([]);
      }
    } catch {
      setItems([]);
    }
  };

  // Fetch list of products for dropdown
  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${apiUrl}/inventory_crud.php?products=1`);
      if (Array.isArray(res.data) && res.data[0] === 'success') {
        setProducts(res.data[1]);
      }
    } catch {}
  };

  // Handle add item to PO
  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItem.device_id || !newItem.quantity || !newItem.price) {
      toast.error('Fill out all fields');
      return;
    }
    try {
      const body = {
        status: 'addPOItem',
        data: {
          po_id: id,
          device_id: newItem.device_id,
          quantity: newItem.quantity,
          price: newItem.price,
        }
      };
      const res = await axios.post(`${apiUrl}/inventory_crud.php`, body);
      if (res.data.success) {
        toast.success('Item added!');
        setNewItem({ device_id: '', quantity: '', price: '' });
        fetchPOItems();
      } else {
        toast.error(res.data.error || 'Failed to add item');
      }
    } catch {
      toast.error('API error');
    }
  };

  const updatePOStatus = async (newStatus) => {
    if (!window.confirm(`Are you sure you want to mark this PO as "${newStatus}"?`)) return;
    try {
      const res = await axios.post(`${apiUrl}/inventory_crud.php`, {
        status: 'updatePOStatus',
        data: { po_id: po.id, status: newStatus },
      });
      if (res.data.success) {
        toast.success(`PO status updated to ${newStatus}`);
        // Optionally, refetch PO data to update status on page:
        fetchPO(); // replace with your fetch PO function
      } else {
        toast.error(res.data.error || 'Failed to update PO status');
      }
    } catch (e) {
      toast.error('Server error');
    }
  };

  const handleStatusChangeRequest = (newStatus) => {
    setPendingStatus(newStatus);
    setShowConfirmModal(true);
  };

  const createStageFromPO = async (poObj) => {
    const hasJob = !!poObj?.job_number;
    const hasSvc = !!poObj?.service_log_number;

    if (!hasJob && !hasSvc) {
      toast.warn('PO has no Job # or Service Log # — skipping Stage creation.');
      return;
    }

    try {
      const res = await axios.post(
        `${apiUrl}/inventory_crud.php`,
        {
          status: 'addStaging',
          data: {
            job_number: hasJob ? poObj.job_number : null,
            service_log_number: !hasJob && hasSvc ? poObj.service_log_number : null,
            stage_date: new Date().toISOString().slice(0, 10),
            notes: `Auto-created from PO #${poObj?.po_number}${
              hasJob ? ` (Job ${poObj.job_number})` : hasSvc ? ` (Svc ${poObj.service_log_number})` : ''
            }`,
            // created_by is set by backend (logged-in user)
          },
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const ok =
        (res.data && res.data.success === true) ||
        (Array.isArray(res.data) && res.data[0] === 'success');

      if (ok) {
        toast.success(`Stage created from PO ${poObj?.po_number}`);
        // Optional: navigate(`/stage/${res.data.stage_id}`);
      } else {
        toast.error(res.data?.error || 'Failed to create Stage from PO');
      }
    } catch {
      toast.error('API error creating Stage from PO');
    }
  };


  const handleConfirmStatusChange = async () => {
    try {
      const res = await axios.post(
        `${apiUrl}/inventory_crud.php`,
        {
          status: 'updatePOStatus',
          data: { po_id: po.id, status: pendingStatus }, // aligned payload
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const ok =
        (res.data && res.data.success === true) ||
        (Array.isArray(res.data) && res.data[0] === 'success');

      if (ok) {
        toast.success(`PO marked as ${pendingStatus}`);

        // Create a Stage only when the PO is closed
        if (pendingStatus === 'Closed') {
          await createStageFromPO(po);
        }

        // Refresh data
        await Promise.all([fetchPO(), fetchPOItems(), fetchProducts()]);
      } else {
        toast.error(res.data?.error || 'Failed to update status');
      }
    } catch {
      toast.error('Error updating status');
    } finally {
      setShowConfirmModal(false);
      setPendingStatus(null);
    }
  };

  const handleCancelStatusChange = () => {
    setShowConfirmModal(false);
    setPendingStatus(null);
  };

  const handleDeleteItem = async (itemId) => {
    try {
      const res = await axios.post(`${apiUrl}/inventory_crud.php`, {
        status: 'deletePOItem',
        id: itemId
      });

      if (res.data && res.data.success) {
        setItems(prev => prev.filter(item => String(item.id) !== String(itemId)));
        toast.success('Item deleted');
      } else {
        toast.error(res.data?.error || 'Failed to delete item');
      }
    } catch (e) {
      toast.error('API error: Could not delete item');
    }
  };
 

  if (loading) return <div className="p-8">Loading...</div>;
  if (!po) return <div className="p-8 text-red-600 space-x-4">

    <button
      className="text-blue-500 hover:underline"
      onClick={() => navigate('/inventory_dashboard', { state: { tab: 'Purchase Orders' } })}
    >
      &larr; Back to PO List
    </button>
    <span className="text-red-600">PO Not Found</span>
  </div>;

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-md font-bold">Purchase Order #{po.po_number}</h1>
        <button
          className="text-blue-500 hover:underline"
          onClick={() => navigate('/inventory_dashboard', { state: { tab: 'Purchase Orders' } })}
        >
          &larr; Back to PO List
        </button>
      </div>

      { /* Status and Actions */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => window.open(`/print/po/${po.id}`, '_blank')}
          className="bg-indigo-700 text-white px-4 py-2 rounded hover:bg-indigo-900"
        >
          Printable View
        </button>
        {po.status !== 'Cancelled' && po.status !== 'Closed' && (
          <button
            onClick={() => handleStatusChangeRequest('Cancelled')}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-800"
          >
            Cancel
          </button>
        )}
        { po.status !== 'Ordered' && po.status !== 'Closed' && items.length !== 0 && (
          <button
            onClick={() => handleStatusChangeRequest('Ordered')}
            className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-700"
          >
            Ordered
          </button>
        )}        
        {po.status !== 'Closed' && po.status !== 'Open' && (
          <button
            onClick={() => handleStatusChangeRequest('Open')}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-700"
          >
            Open
          </button>
        )}
        { po.status !== 'Closed' && items.length !== 0 && (
          <button
            onClick={() => handleStatusChangeRequest('Closed')}
            className="bg-green-700 text-white px-4 py-2 rounded hover:bg-green-900"
          >
            Close PO
          </button>
        )}
      </div>

      {/* Header Section */}
      <div className="bg-gray-50 p-4 rounded-md mb-6 border flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1">
          <div className="font-semibold">Vendor:</div>
          <div className="text-gray-700">{po.vendor}</div>
        </div>
        {po.job_number && (
          <div className="flex-1">
            <div className="font-semibold">Job #:</div>
            <div className="text-gray-700">{po.job_number}</div>
          </div>
        )}
        <div className="flex-1">
          <div className="font-semibold">Status:</div>
          <div className={`text-gray-700 ${po.status === 'Closed' ? 'text-green-700 font-extrabold' : ''}`}>{po.status}</div>
        </div>
        <div className="flex-1">
          <div className="font-semibold">Expected Date:</div>
          <div className="text-gray-700">{po.expected_date || '-'}</div>
        </div>
        <div className="flex-1">
          <div className="font-semibold">Created:</div>
          <div className="text-gray-700">{po.date_created}</div>
        </div>
      </div>

      {/* PO Notes */}
      <div className="mb-6">
        <div className="font-semibold">Notes:</div>
        <div className="text-gray-700">{po.notes || <span className="italic text-gray-400">No notes</span>}</div>
      </div>

      {/* Line Items */}
      <div className="bg-white border rounded-lg shadow p-4 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-bold text-lg">Line Items</h2>
          {/* Add more action buttons here if needed */}
        </div>
        <table className="min-w-full table-auto border border-gray-300 rounded-lg">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-4 py-2 text-left text-sm font-semibold">Device</th>
              <th className="border px-4 py-2 text-left text-sm font-semibold">Description</th>
              <th className="border px-4 py-2 text-right text-sm font-semibold">Qty</th>
              <th className="border px-4 py-2 text-right text-sm font-semibold">Price</th>
              <th className="border px-4 py-2 text-right text-sm font-semibold">Total</th>
              <th className="border text-center text-sm font-semibold">A</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center text-gray-400 py-4">
                  No items found.
                </td>
              </tr>
            ) : (
              items.map((item, idx) => {
                const product = products.find(p => String(p.id) === String(item.device_id));
                return (
                  <tr key={item.id}>                   
                    <td className="border px-4 py-2">{product ? product.device_name : item.device_id}</td>
                    <td className="border px-4 py-2">{product ? product.full_description : ''}</td>
                    <td className="border px-4 py-2 text-right">{item.quantity}</td>
                    <td className="border px-4 py-2 text-right">${parseFloat(item.price).toFixed(2)}</td>
                    <td className="border px-4 py-2 text-right font-semibold">
                      ${(item.quantity * item.price).toFixed(2)}
                    </td>
                    <td className="border px-2 text-center">
                      { po.status !== 'Closed' && (
                      <button
                        onClick={() => handleDeleteItem(item.id)}
                        className="hover:bg-red-100 rounded p-1"
                        title="Delete"
                      >
                        <TrashIcon className="h-5 w-5 text-red-500 hover:text-red-700 inline" />
                      </button>
                      )}
                    </td>                     
                  </tr>
                )
              })
            )}

            {/* Add item row */}
            <tr>
              <td className="border px-4 py-2">
                <select
                  className="w-full border rounded"
                  value={newItem.device_id}
                  onChange={e => setNewItem({ ...newItem, device_id: e.target.value })}
                >
                  <option value="">Select Device</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.device_name} :: {p.manufacture}</option>
                  ))}
                </select>
              </td>
              <td className="border px-4 py-2">
                {newItem.device_id ? (products.find(p => String(p.id) === newItem.device_id)?.full_description || '') : ''}
              </td>
              <td className="border px-4 py-2 text-right">
                <input
                  type="number"
                  className="w-20 border rounded text-right"
                  min={1}
                  value={newItem.quantity}
                  onChange={e => setNewItem({ ...newItem, quantity: e.target.value })}
                />
              </td>
              <td className="border px-4 py-2 text-right">
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  className="w-24 border rounded text-right"
                  value={newItem.price}
                  onChange={e => setNewItem({ ...newItem, price: e.target.value })}
                />
              </td>
              <td className="border px-4 py-2 text-right">
                <button
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
                  onClick={handleAddItem}
                >
                  Add Item
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
 
      <ConfirmDeleteModal
        isOpen={showConfirmModal}
        onConfirm={handleConfirmStatusChange}
        onCancel={handleCancelStatusChange}
        title={`Confirm ${pendingStatus}`}
        message={`Are you sure you want to mark this PO as ${pendingStatus}?`}
        itemName={po ? `PO #: ${po.po_number}` : ''}
      />

    </div>
  );
};

export default POViewPage;
